import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { superAdminApi } from '../services/super-admin-api'

// Local types for the Supabase-based implementation
export interface OrganizationMember {
  id: string
  organization_id: string
  profile_id: string
  role: 'admin' | 'professional' | 'assistant'
  permissions: Record<string, boolean>
  is_active: boolean
  joined_at: string
  profile: {
    id: string
    email: string
    full_name: string
    phone: string | null
    auth_user_id: string | null
    created_at: string
  }
}

interface MembersResponse {
  data: OrganizationMember[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

interface ListMembersParams {
  organization_id: string
  page?: number
  page_size?: number
  role?: string
  is_active?: boolean
}

interface AddMemberData {
  organization_id: string
  email: string
  full_name: string
  phone?: string
  role: 'admin' | 'professional' | 'assistant'
  send_invite?: boolean
  password?: string
}

interface UpdateMemberData {
  role?: 'admin' | 'professional' | 'assistant'
  is_active?: boolean
  permissions?: Record<string, boolean>
}

interface OrganizationMemberRow {
  organization_id: string
  profile_id: string
  role: OrganizationMember['role']
  permissions: Record<string, boolean> | null
  is_active: boolean
  joined_at: string
  profiles: OrganizationMember['profile']
}

export function useOrganizationMembers(params: ListMembersParams) {
  const { organization_id, page = 1, page_size = 20, role, is_active } = params

  return useQuery<MembersResponse>({
    queryKey: ['super-admin', 'members', params],
    queryFn: async () => {
      let query = supabase
        .from('organization_members')
        .select('*, profiles!inner(id, email, full_name, phone, auth_user_id, created_at)', { count: 'exact' })
        .eq('organization_id', organization_id)
        .order('joined_at', { ascending: false })

      if (role) query = query.eq('role', role)
      if (is_active !== undefined) query = query.eq('is_active', is_active)

      // Pagination
      const from = (page - 1) * page_size
      const to = from + page_size - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      const members: OrganizationMember[] = ((data || []) as OrganizationMemberRow[]).map((member) => ({
        id: member.profile_id,
        organization_id: member.organization_id,
        profile_id: member.profile_id,
        role: member.role,
        permissions: member.permissions || {},
        is_active: member.is_active,
        joined_at: member.joined_at,
        profile: {
          id: member.profiles.id,
          email: member.profiles.email,
          full_name: member.profiles.full_name,
          phone: member.profiles.phone,
          auth_user_id: member.profiles.auth_user_id,
          created_at: member.profiles.created_at,
        },
      }))

      const total = count || 0
      const total_pages = Math.ceil(total / page_size)

      return {
        data: members,
        pagination: { page, page_size, total, total_pages },
      }
    },
    enabled: !!organization_id,
  })
}

export function useAddMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: AddMemberData) => {
      // If password provided, use backend API (creates auth user + profile + org member in one shot)
      if (data.password) {
        const result = await superAdminApi.post<{ data: unknown; temporaryPassword: string }>('/users', {
          email: data.email,
          full_name: data.full_name,
          role: data.role,
          organization_id: data.organization_id,
          password: data.password,
        })
        return result
      }

      // No password: create profile only (user will be pending until invited)
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', data.email)
        .maybeSingle()

      let profileId: string

      if (existingProfile) {
        // Profile exists, check if already a member
        const { data: existingMember } = await supabase
          .from('organization_members')
          .select('profile_id')
          .eq('organization_id', data.organization_id)
          .eq('profile_id', existingProfile.id)
          .maybeSingle()

        if (existingMember) {
          throw new Error('Este usuário já é membro desta organização')
        }

        profileId = existingProfile.id
      } else {
        // Create new profile (without auth user — pending status)
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            email: data.email,
            full_name: data.full_name,
            phone: data.phone || null,
            role: data.role === 'admin' ? 'admin' : 'professional',
          })
          .select()
          .single()

        if (profileError) throw profileError
        profileId = newProfile.id
      }

      // Add as organization member
      const { data: member, error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: data.organization_id,
          profile_id: profileId,
          role: data.role,
          is_active: true,
        })
        .select('*, profiles(id, email, full_name, phone, auth_user_id, created_at)')
        .single()

      if (memberError) throw memberError

      return member
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'members'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organization', variables.organization_id] })
    },
  })
}

export function useUpdateMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, organization_id, data }: { id: string; organization_id: string; data: UpdateMemberData }) => {
      const updateData: Record<string, unknown> = {}

      if (data.role !== undefined) updateData.role = data.role
      if (data.is_active !== undefined) updateData.is_active = data.is_active
      if (data.permissions !== undefined) updateData.permissions = data.permissions

      const { data: member, error } = await supabase
        .from('organization_members')
        .update(updateData)
        .eq('organization_id', organization_id)
        .eq('profile_id', id)
        .select('*, profiles(id, email, full_name, phone, auth_user_id, created_at)')
        .single()

      if (error) throw error
      return { member, organization_id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'members'] })
    },
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, organization_id }: { id: string; organization_id: string }) => {
      // Soft delete - just deactivate the member
      const { error } = await supabase
        .from('organization_members')
        .update({ is_active: false })
        .eq('organization_id', organization_id)
        .eq('profile_id', id)

      if (error) throw error
      return { organization_id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'members'] })
    },
  })
}

export function useResetMemberPassword() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      // Use Supabase's password reset functionality
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      })

      if (error) throw error
      return { email }
    },
  })
}

export function useInviteMember() {
  return useMutation({
    mutationFn: async ({ email, name, organization_id }: { email: string; name: string; organization_id: string }) => {
      return superAdminApi.post<{
        success: boolean
        message: string
        email: string
        emailSent: boolean
        temporaryPassword: string | null
      }>('/users/send-invite', {
        email,
        name,
        organization_id,
      })
    },
  })
}

// Hook para impersonar usuário via API do super-admin
interface ImpersonateResponse {
  data: {
    session_token: string
    expires_at: string
    action_url?: string
  }
}

export function useImpersonateUser() {
  return useMutation({
    mutationFn: async ({ profileId }: { profileId: string }) => {
      const response = await superAdminApi.post<ImpersonateResponse>(`/users/${profileId}/impersonate`)
      return response.data
    },
  })
}

// Hook para reset manual de senha (retorna senha temporária)
interface AdminResetPasswordResponse {
  success: boolean
  message: string
  temporaryPassword: string
  email: string
  note: string
}

export function useAdminResetPassword() {
  return useMutation({
    mutationFn: async ({ profileId }: { profileId: string }) => {
      const response = await superAdminApi.post<AdminResetPasswordResponse>(`/users/${profileId}/reset-password`)
      return response
    },
  })
}
